import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight, Buildings, ChalkboardTeacher, Package, Target } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { AAROH_IMAGES } from "../../data/images.js";
import { opportunityPaths } from "../../data/content.js";
import { RevealItem, RollButton, Rise, mailto } from "./shared.jsx";
import "./join.css";

const OFFERS = [
  {
    Icon: ChalkboardTeacher,
    title: "Mentoring",
    give: "Mentor a student team in an area you know well.",
    get: "Stronger feedback while they build.",
  },
  {
    Icon: Target,
    title: "Real problems",
    give: "Share a scoped problem students can investigate with appropriate support.",
    get: "Context they could not create alone.",
  },
  {
    Icon: Package,
    title: "Resources",
    give: "Programme resources or access to equipment.",
    get: "Support for programme access.",
  },
  {
    Icon: Buildings,
    title: "Venues",
    give: "A room or venue where students can build and present.",
    get: "A place to test useful work.",
  },
];

const INCLUDE = [
  "Who you represent",
  "What you can contribute",
  "When you are available",
  "Any conditions attached",
  "One event, or a longer collaboration",
];

const CREDITS = [
  ["Tale of Humankind Foundation", "Organiser", "Metamorphosis"],
  ["IIT Delhi, Office of Academic Outreach & New Initiatives", "Organiser", "Metamorphosis"],
  ["Project GRID", "Outreach partner", "Metamorphosis"],
  ["Tachyon India", "Collaborator", "AAROH"],
  ["CiPD, IIIT Delhi", "Institutional collaborator", "AAROH"],
  ["Stacks KB, Fotocart, .xyz, GitHub, GradCircle, Skills4U", "Supporters", "AAROH"],
  ["HP", "Prize laptops", "Aurora"],
];

const proposal = mailto("Partnership proposal");

function OfferRow({ offer, index }) {
  const reduce = useReducedMotion();
  const { Icon } = offer;
  return (
    <li className="jn-offer">
      <div className="jn-offer__give">
        <span className="jn-offer__icon" aria-hidden="true"><Icon weight="bold" /></span>
        <h3>{offer.title}</h3>
        <p>{offer.give}</p>
      </div>
      <svg className="jn-offer__wire" viewBox="0 0 200 20" preserveAspectRatio="none" aria-hidden="true">
        <motion.path
          d="M 0 10 H 196"
          vectorEffect="non-scaling-stroke"
          initial={reduce ? false : { pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{ duration: 0.9, delay: 0.15 + index * 0.04, ease: EASE_OUT }}
        />
        <motion.path
          d="M 188 3 L 197 10 L 188 17"
          vectorEffect="non-scaling-stroke"
          initial={reduce ? false : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{ duration: 0.3, delay: 0.95 + index * 0.04 }}
        />
      </svg>
      <motion.div
        className="jn-offer__get"
        initial={reduce ? false : { opacity: 0, x: -16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.8 }}
        transition={{ duration: 0.7, delay: 0.75 + index * 0.04, ease: EASE_OUT }}
      >
        <span className="jn-mono">Students get</span>
        <p>{offer.get}</p>
      </motion.div>
    </li>
  );
}

export default function ForPartnersPage() {
  const steps = opportunityPaths.find((item) => item.slug === "organisations").steps;
  return (
    <PageFrame className="rd-page jn-page jn-pt">
      <Seo
        title="For partners"
        path="/for-partners"
        description="Community organisations, mentors and institutions can help students build with better context and stronger feedback."
      />
      <main>
        <header className="jn-wrap jn-pt-hero">
          <Rise as="p" className="jn-mono" delay={0.05}>For mentors and partners</Rise>
          <MaskWords as="h1" className="jn-h1 jn-pt-hero__title" text="Bring real problems into the room." onMount delay={0.1} />
          <div className="jn-pt-hero__row">
            <Rise className="jn-pt-hero__copy" delay={0.4}>
              <p className="jn-lede">
                Community organisations, mentors and institutions can help students build with better context and stronger feedback. Start with a specific contribution and a clear idea of who it would help.
              </p>
              <div className="gh-actions">
                <RollButton href={proposal} label="Email a partnership proposal" />
                <RollButton to="/partnerships" label="See existing relationships" variant="ghost" />
              </div>
            </Rise>
            <ClipPhoto
              className="jn-pt-hero__photo"
              src={AAROH_IMAGES.collaboration}
              alt="Organisers, collaborators and students on stage together at AAROH, IIIT Delhi"
              caption="AAROH at IIIT Delhi. Students, mentors and organisers together."
              sizes="(max-width: 999px) 100vw, 45vw"
              priority
            />
          </div>
        </header>

        <section className="jn-wrap jn-pt-offers" aria-labelledby="jn-pt-offers-title">
          <header className="jn-pt-offers__head">
            <MaskWords id="jn-pt-offers-title" className="jn-h2" text="Four things you can offer." />
            <p className="jn-lede">Each one connects to something students need while they build.</p>
          </header>
          <ol className="jn-offers">
            {OFFERS.map((offer, index) => <OfferRow key={offer.title} offer={offer} index={index} />)}
          </ol>
        </section>

        <section className="jn-wrap jn-pt-include" aria-labelledby="jn-pt-include-title">
          <div>
            <MaskWords id="jn-pt-include-title" className="jn-h3" text="What to put in your first email." />
            <p>Explain whether you are proposing one event or a longer collaboration. A short, specific note is easier to act on than a long general one.</p>
          </div>
          <ul>
            {INCLUDE.map((item, index) => (
              <RevealItem key={item} delay={index * 0.05}>
                <span className="jn-tick" aria-hidden="true" />{item}
              </RevealItem>
            ))}
          </ul>
        </section>

        <section className="jn-wrap jn-pt-agree" aria-labelledby="jn-pt-agree-title">
          <MaskWords id="jn-pt-agree-title" className="jn-h2" text="How a collaboration is agreed." />
          <ol className="jn-pt-agree__steps">
            {steps.map((step, index) => (
              <RevealItem key={step} delay={index * 0.08}>
                <span className="jn-pt-agree__num">{String(index + 1).padStart(2, "0")}</span>
                <strong>{step}</strong>
              </RevealItem>
            ))}
          </ol>
          <p className="jn-pt-agree__note">
            Programme responsibilities, participant safeguards, costs, credits and any use of names or logos are agreed before a collaboration is announced.
          </p>
        </section>

        <section className="jn-pt-credit" data-nav-tone="dark" aria-labelledby="jn-pt-credit-title">
          <div className="jn-wrap">
            <p className="jn-mono jn-mono--dark">How partners are credited</p>
            <MaskWords id="jn-pt-credit-title" className="jn-h2 jn-pt-credit__title" text="We credit the part you played." accent="credit" />
            <p className="jn-pt-credit__lede">
              Our programme pages separate organisers, outreach partners, collaborators and supporters. Here is how each one is credited.
            </p>
            <ul className="jn-ledger">
              <li className="jn-ledger__head" aria-hidden="true">
                <span>Organisation</span><span>Credited as</span><span>Programme</span>
              </li>
              {CREDITS.map(([name, role, programme]) => (
                <li key={name}>
                  <strong>{name}</strong>
                  <span className="jn-ledger__role">{role}</span>
                  <span className="jn-ledger__prog">{programme}</span>
                </li>
              ))}
            </ul>
            <ul className="jn-pt-credit__rules">
              <li>A meeting is not an endorsement or a partnership.</li>
              <li>Each logo sits under the programme it belongs to, with the relationship named.</li>
            </ul>
            <Link className="gh-link jn-link--dark" to="/partnerships">See every programme credit <ArrowRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </section>

        <section className="jn-wrap jn-pt-end" aria-labelledby="jn-pt-end-title">
          <MaskWords id="jn-pt-end-title" className="jn-h2" text="Start with one clear note." />
          <a className="jn-pt-end__mail" href={proposal}>
            <span>Email a partnership proposal</span>
            <ArrowUpRight weight="bold" aria-hidden="true" />
          </a>
          <p className="jn-small">Opens your email app with the subject "Partnership proposal". Organisations can also read <Link to="/student-opportunities/organisations">the organisation route</Link>.</p>
        </section>
      </main>
    </PageFrame>
  );
}
